import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGlynt from '../../hooks/useGlynt';
import { IRQCallbacks } from '../../../types/my-types';
import { IRulesetListResponse } from '../../../types/ruleset-types';

interface DeleteRulesetParams {
  dataPoolId: string;
  rulesetId: string;
}

export function useDeleteRuleset(callbacks?: IRQCallbacks) {
  const glynt = useGlynt();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dataPoolId, rulesetId }: DeleteRulesetParams) =>
      (await glynt).ruleset.del(dataPoolId, rulesetId),
    onSuccess: (data, variables) => {
      // A successful delete can briefly be followed by a stale list response on
      // eventually consistent environments. Update every matching cached page
      // directly so that an acknowledged deletion is not reinserted by an
      // immediate refetch.
      queryClient.setQueriesData<IRulesetListResponse>(
        { queryKey: ['rulesets', variables.dataPoolId] },
        (cached) => {
          if (!cached || !cached.results.some((ruleset) => ruleset.id === variables.rulesetId)) {
            return cached;
          }

          return {
            ...cached,
            count: Math.max(0, cached.count - 1),
            results: cached.results.filter((ruleset) => ruleset.id !== variables.rulesetId),
          };
        }
      );
      callbacks?.onSuccess?.(data);
    },
    onError: callbacks?.onError,
    onMutate: callbacks?.onMutate,
    onSettled: callbacks?.onSettled,
  });
}
